<script lang="ts">
	import { onMount } from 'svelte';
	import { appStore } from '../../stores/appStore';
	import { get } from 'svelte/store';

	let selectedPeriod: 'day' | 'week' | 'month' | 'year' = 'week';
	let metrics = {
		newUsers: 0,
		newPayments: 0,
		newRepositories: 0
	};
	let loading = false;
	let error = '';

	async function fetchMetrics() {
		loading = true;
		error = '';

		try {
			const client = get(appStore).client;
			const token = get(appStore).token;

			if (!client || !token) {
				error = 'Not authenticated';
				return;
			}

			const response = await client.getDashboardMetrics({
				token,
				period: selectedPeriod
			});

			if (response.response.successFull && response.response.metrics) {
				metrics = {
					newUsers: response.response.metrics.newUsers,
					newPayments: response.response.metrics.newPayments,
					newRepositories: response.response.metrics.newRepositories
				};
			} else {
				error = response.response.reason || 'Failed to fetch metrics';
			}
		} catch (e: any) {
			error = e.message || 'An error occurred while fetching metrics';
			console.error('Failed to fetch metrics:', e);
		} finally {
			loading = false;
		}
	}

	onMount(() => {
		fetchMetrics();
	});

	$: if (selectedPeriod) {
		fetchMetrics();
	}
</script>

<div class="metrics-container">
	<div class="metrics-header">
		<h2>Platform Metrics</h2>
		<div class="period-selector">
			<button
				class="period-btn"
				class:active={selectedPeriod === 'day'}
				on:click={() => (selectedPeriod = 'day')}>Day</button
			>
			<button
				class="period-btn"
				class:active={selectedPeriod === 'week'}
				on:click={() => (selectedPeriod = 'week')}>Week</button
			>
			<button
				class="period-btn"
				class:active={selectedPeriod === 'month'}
				on:click={() => (selectedPeriod = 'month')}>Month</button
			>
			<button
				class="period-btn"
				class:active={selectedPeriod === 'year'}
				on:click={() => (selectedPeriod = 'year')}>Year</button
			>
		</div>
	</div>

	{#if loading}
		<div class="loading">Loading metrics...</div>
	{:else if error}
		<div class="error">{error}</div>
	{:else}
		<div class="metrics-grid">
			<div class="metric-card">
				<div class="metric-icon users">
					<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
						<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
						<circle cx="9" cy="7" r="4"></circle>
						<path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
						<path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
					</svg>
				</div>
				<div class="metric-content">
					<p class="metric-label">New Users</p>
					<p class="metric-value">{metrics.newUsers.toLocaleString()}</p>
				</div>
			</div>

			<div class="metric-card">
				<div class="metric-icon payments">
					<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
						<rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
						<line x1="1" y1="10" x2="23" y2="10"></line>
					</svg>
				</div>
				<div class="metric-content">
					<p class="metric-label">New Payments</p>
					<p class="metric-value">{metrics.newPayments.toLocaleString()}</p>
				</div>
			</div>

			<div class="metric-card">
				<div class="metric-icon repos">
					<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
						<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
					</svg>
				</div>
				<div class="metric-content">
					<p class="metric-label">New Repositories</p>
					<p class="metric-value">{metrics.newRepositories.toLocaleString()}</p>
				</div>
			</div>
		</div>
	{/if}
</div>

<style>
	.metrics-container {
		margin-bottom: 2rem;
	}

	.metrics-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 1.5rem;
		flex-wrap: wrap;
		gap: 1rem;
	}

	.metrics-header h2 {
		font-size: 1.5rem;
		font-weight: 600;
		margin: 0;
		color: var(--text-primary, #e6edf3);
	}

	.period-selector {
		display: flex;
		gap: 0.5rem;
		background: var(--bg-secondary, #161b22);
		padding: 0.25rem;
		border-radius: 6px;
	}

	.period-btn {
		padding: 0.5rem 1rem;
		border: none;
		background: transparent;
		color: var(--text-secondary, #8b949e);
		border-radius: 4px;
		cursor: pointer;
		font-size: 0.875rem;
		font-weight: 500;
		transition: all 0.2s ease;
	}

	.period-btn:hover {
		background: var(--bg-tertiary, #1c2128);
		color: var(--text-primary, #e6edf3);
	}

	.period-btn.active {
		background: var(--accent-primary, #238636);
		color: #ffffff;
	}

	.metrics-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
		gap: 1rem;
	}

	.metric-card {
		display: flex;
		align-items: center;
		gap: 1rem;
		padding: 1.5rem;
		background: var(--bg-secondary, #161b22);
		border: 1px solid var(--border-primary, #30363d);
		border-radius: 8px;
		transition: transform 0.2s ease, box-shadow 0.2s ease;
	}

	.metric-card:hover {
		transform: translateY(-2px);
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	}

	.metric-icon {
		width: 48px;
		height: 48px;
		border-radius: 8px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.metric-icon.users {
		background: rgba(88, 166, 255, 0.1);
		color: #58a6ff;
	}

	.metric-icon.payments {
		background: rgba(126, 184, 69, 0.1);
		color: #7eb845;
	}

	.metric-icon.repos {
		background: rgba(201, 97, 255, 0.1);
		color: #c961ff;
	}

	.metric-content {
		flex: 1;
	}

	.metric-label {
		font-size: 0.875rem;
		color: var(--text-secondary, #8b949e);
		margin: 0 0 0.25rem 0;
	}

	.metric-value {
		font-size: 2rem;
		font-weight: 600;
		color: var(--text-primary, #e6edf3);
		margin: 0;
	}

	.loading,
	.error {
		padding: 2rem;
		text-align: center;
		background: var(--bg-secondary, #161b22);
		border: 1px solid var(--border-primary, #30363d);
		border-radius: 8px;
	}

	.error {
		color: var(--danger, #f85149);
	}
</style>
